import React, {useEffect, useState} from 'react'
import {useParams} from 'react-router-dom'
import {Box} from '@mui/material'
import {STATUS} from 'src/constants/constants'
import apiClient from 'src/services/apiClient'
import AuthLayout from 'src/components-new/layout/AuthLayout'
import EmptyState from 'src/components-new/layout/EmptyState'
import Button from 'src/components-new/primitives/Button'
import InlineMessage from 'src/components-new/feedback/InlineMessage'
import DataStep from './steps/DataStep'
import ConsentsStep, {ConsentsStepValue} from './steps/ConsentsStep'
import EmailSentStep from './steps/EmailSentStep'
import {
  AddressValue,
  buildPatchBody,
  CaregiverValue,
  ConsentOption,
  isValidEmail,
  isValidPassword,
  isValidPostalCode,
  isValidUsername,
  MainDataValue,
  validateConsentOption,
} from './patientDigitalEnrollmentValidation'

interface LoginConfiguration {
  scope?: string
  logoPSP?: string
  PSPTitleAndIcon?: {title: string, icon: string}
  informationHtml?: string
  consentHtml?: string
  consentCheckboxOptions?: ConsentOption[]
}

interface PatientDigitalEnrollmentPageNewProps {
  config: LoginConfiguration
}

const INITIAL_MAIN_DATA: MainDataValue = {
  firstName: '',
  lastName: '',
  sex: '',
  birthDate: null,
  email: '',
  cellPhoneNumber: '',
  preferredContactMethods: [],
  username: '',
  password: '',
  confirmPassword: '',
}

const INITIAL_ADDRESS: AddressValue = {
  country: 'Italia',
  region: '',
  province: '',
  city: '',
  address: '',
  streetNumber: '',
  postalCode: '',
  locality: '',
}

const INITIAL_CAREGIVER: CaregiverValue = {
  caregiverFirstName: '',
  caregiverLastName: '',
  caregiverPhoneNumber: '',
  caregiverCellPhoneNumber: '',
  caregiverEmail: '',
}

/**
 * New-style equivalent of src/pages/patientDigitalEnrollment/PatientDigitalEnrollment.tsx.
 * Reorganizes the legacy's single antd Form spanning multiple screens into 2 explicit
 * steps (Dati e indirizzo / Consensi) + the same request.state-driven terminal screens.
 * Data/API contract is unchanged — see `buildPatchBody` for the exact submitted shape.
 */
export default function PatientDigitalEnrollmentPageNew({config}: PatientDigitalEnrollmentPageNewProps): JSX.Element {
  const {id} = useParams<{id: string}>()

  const [request, setRequest] = useState<any>()
  const [requestStatus, setRequestStatus] = useState<string>()
  const [currentStep, setCurrentStep] = useState(0)
  const [mainData, setMainData] = useState<MainDataValue>(INITIAL_MAIN_DATA)
  const [address, setAddress] = useState<AddressValue>(INITIAL_ADDRESS)
  const [caregiver, setCaregiver] = useState<CaregiverValue>(INITIAL_CAREGIVER)
  const [consents, setConsents] = useState<ConsentsStepValue>({consents: {}, consentPassword: ''})
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [checkingField, setCheckingField] = useState(false)
  const [submitStatus, setSubmitStatus] = useState<string>()

  const consentOptions: ConsentOption[] = config.consentCheckboxOptions ?? []

  const fetchRequest = async () => {
    const response = await apiClient.getByIdDigitalEnrollment(id, setRequestStatus)
    setRequest(response)
    if (response) {
      setMainData((prev) => ({
        ...prev,
        firstName: response.firstName ?? '',
        lastName: response.lastName ?? '',
        email: response.email ?? '',
        cellPhoneNumber: response.cellPhoneNumber ?? '',
      }))
    }
  }

  useEffect(() => {
    if (config.PSPTitleAndIcon?.title) document.title = config.PSPTitleAndIcon.title
    const link = document.querySelector('link[rel="shortcut icon"]')
    if (link && config.PSPTitleAndIcon?.icon) {
      link.setAttribute('href', `data:image/png;base64,${config.PSPTitleAndIcon.icon}`)
    }
    fetchRequest()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id])

  useEffect(() => {
    if (submitStatus === STATUS.FULFILLED) fetchRequest()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [submitStatus])

  const validateDataStep = async (): Promise<boolean> => {
    const nextErrors: Record<string, string> = {}

    if (!mainData.sex) nextErrors.sex = 'Questo campo è obbligatorio'
    if (!mainData.email) {
      nextErrors.email = 'Questo campo è obbligatorio'
    } else if (!isValidEmail(mainData.email)) {
      nextErrors.email = 'Formato email non valido'
    }
    if (!mainData.username) {
      nextErrors.username = 'Questo campo è obbligatorio'
    } else if (!isValidUsername(mainData.username)) {
      nextErrors.username = 'Formato username non valido'
    }
    if (!mainData.password) {
      nextErrors.password = 'Questo campo è obbligatorio'
    } else if (!isValidPassword(mainData.password)) {
      nextErrors.password = 'Password troppo debole'
    }
    if (!mainData.confirmPassword) {
      nextErrors.confirmPassword = 'Questo campo è obbligatorio'
    } else if (mainData.confirmPassword !== mainData.password) {
      nextErrors.confirmPassword = 'Le due password non corrispondono!'
    }

    if (!address.region) nextErrors.region = 'Questo campo è obbligatorio'
    if (!address.province) nextErrors.province = 'Questo campo è obbligatorio'
    if (!address.city) nextErrors.city = 'Questo campo è obbligatorio'
    if (!address.address) nextErrors.address = 'Questo campo è obbligatorio'
    if (!address.streetNumber) nextErrors.streetNumber = 'Questo campo è obbligatorio'
    if (!address.postalCode) {
      nextErrors.postalCode = 'Questo campo è obbligatorio'
    } else if (!isValidPostalCode(address.postalCode)) {
      nextErrors.postalCode = 'Formato codice postale non valido'
    }

    if (caregiver.caregiverEmail && !isValidEmail(caregiver.caregiverEmail)) {
      nextErrors.caregiverEmail = 'Formato email non valido'
    }

    if (Object.keys(nextErrors).length > 0) {
      setErrors(nextErrors)
      return false
    }

    setCheckingField(true)
    try {
      const isEmailValid = await apiClient.checkEmail(mainData.email, () => undefined)
      if (!isEmailValid) {
        setErrors({email: 'Email non disponibile'})
        return false
      }
      const isUsernameValid = await apiClient.checkUsername(mainData.username, () => undefined)
      if (!isUsernameValid) {
        setErrors({username: 'Username non disponibile'})
        return false
      }
      return true
    } finally {
      setCheckingField(false)
    }
  }

  const validateConsentsStep = (): boolean => {
    const nextErrors: Record<string, string> = {}
    consentOptions.forEach((opt) => {
      const message = validateConsentOption(opt, consents.consents[opt.value])
      if (message) nextErrors[opt.value] = message
    })
    if (!consents.consentPassword) {
      nextErrors.consentPassword = 'Questo campo è obbligatorio'
    } else if (consents.consentPassword !== mainData.password) {
      nextErrors.consentPassword = 'Le password non corrisponde con quella precedenti!'
    }
    setErrors(nextErrors)
    return Object.keys(nextErrors).length === 0
  }

  const goNext = async () => {
    if (!(await validateDataStep())) return
    setErrors({})
    setCurrentStep(1)
  }

  const goBack = () => {
    setErrors({})
    setCurrentStep(0)
  }

  const handleSubmit = async () => {
    if (!validateConsentsStep()) return

    const body = buildPatchBody({mainData, address, caregiver, consentOptions, consents: consents.consents})
    await apiClient.patchDigitalEnrollment(body, id, setSubmitStatus)
  }

  const logo = config.logoPSP ? (
    <img
      alt="logoPSP"
      src={`data:image/png;base64,${config.logoPSP}`}
      style={{maxHeight: 64, maxWidth: 200, objectFit: 'contain'}}
    />
  ) : undefined

  if (requestStatus !== STATUS.FULFILLED || !request) {
    return <AuthLayout logo={logo} maxWidth={720}><Box /></AuthLayout>
  }

  if (request.state === 'PATIENT_COMPILED') {
    return (
      <AuthLayout logo={logo} maxWidth={720}>
        <EmailSentStep requestId={id} />
      </AuthLayout>
    )
  }

  if (request.state !== 'DOCTOR_COMPILED') {
    return (
      <AuthLayout logo={logo} maxWidth={720}>
        <EmptyState
          title="Adesione completata con successo"
          description="Verrà presto contattato da un nostro operatore."
        />
      </AuthLayout>
    )
  }

  const isSubmitting = submitStatus === STATUS.PENDING

  return (
    <AuthLayout logo={logo} maxWidth={720}>
      {submitStatus === STATUS.REJECTED && (
        <InlineMessage severity="error" sx={{mb: 2}}>
          {'Si è verificato un errore'}
        </InlineMessage>
      )}

      {currentStep === 0 && (
        <DataStep
          mainData={mainData}
          onMainDataChange={setMainData}
          address={address}
          onAddressChange={setAddress}
          caregiver={caregiver}
          onCaregiverChange={setCaregiver}
          errors={errors}
          disabled={checkingField}
        />
      )}

      {currentStep === 1 && (
        <ConsentsStep
          informationHtml={config.informationHtml}
          consentHtml={config.consentHtml}
          consentOptions={consentOptions}
          value={consents}
          onChange={setConsents}
          errors={errors}
          disabled={isSubmitting}
        />
      )}

      <Box
        sx={{
          display: 'flex',
          flexDirection: {xs: 'column', sm: 'row'},
          justifyContent: currentStep === 0 ? 'flex-end' : 'space-between',
          gap: 1.5,
          mt: 3,
        }}
      >
        {currentStep === 1 && (
          <Button variant="secondary" onClick={goBack} disabled={isSubmitting}>
            {'Indietro'}
          </Button>
        )}
        <Button
          variant="primary"
          onClick={currentStep === 0 ? goNext : handleSubmit}
          loading={checkingField || isSubmitting}
        >
          {currentStep === 0 ? 'Avanti' : 'Invia'}
        </Button>
      </Box>
    </AuthLayout>
  )
}
